import { useMemo, useState } from 'react';
import { fmt, fmtFull } from './arrShared';
import { ArrDetailModal } from './arrShared';
import { DevOverlay } from '../../components/DevOverlay/DevOverlay';

const REG_COLORS = { NAM: '#1e5fa8', APAC: '#15803d', OEM: '#6d28d9' };

const getPrevMonth = (month) => {
  if (!month) return '';
  const [year, rawMonth] = month.split('-').map(Number);
  if (!year || !rawMonth) return '';
  const prev = new Date(year, rawMonth - 2, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
};

const shortMoney = (value) => {
  const n = Number(value || 0);
  const a = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${Math.round(a / 1e3).toFixed(1)}K`;
  return `${sign}$${Math.round(a).toLocaleString()}`;
};

const axisMoney = (value) => {
  const n = Number(value || 0);
  if (n === 0) return '$0';
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(1)}K`;
  return `${sign}$${Math.round(a).toLocaleString()}`;
};

// Exact port of the reference's fF() — whole-dollar money, no decimals ever
// (fmtFull's 2-decimal Intl currency format doesn't match it). Used wherever
// a reference row/cell is built with fF(), e.g. the T2 modals (ACV / Logos /
// Rep) and the Expansion Candidates table.
const fF = (v) => {
  const n = Number(v || 0);
  return `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;
};

const rowMonthArr = (row, month) => Number(row?.monthly_arr?.[month] || 0);
const EMPTY_ARRAY = [];

const monthRange = (from, to) => {
  if (!from || !to) return [];
  const months = [];
  let [year, month] = from.split('-').map(Number);
  const [endYear, endMonth] = to.split('-').map(Number);
  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
};

const addMonths = (ym, count) => {
  const [year, month] = ym.split('-').map(Number);
  const date = new Date(year, month - 1 + count, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const changeValue = (changes, month, key) => {
  const raw = changes?.[month] || {};
  const canonical = {
    new: ['new', 'New'],
    upsell: ['upsell', 'Upsell'],
    renewal: ['renewal', 'Renewal'],
    churn: ['churn', 'Churn'],
    downsell: ['downsell', 'Downsell', 'DOWNSELL'],
  }[key] || [key];
  return canonical.reduce((sum, name) => sum + Number(raw[name] || 0), 0);
};

function InsightsTab({ data }) {
  const [modal, setModal] = useState(null);
  const [leaderSort, setLeaderSort] = useState({ key: 'arr', asc: false });
  const [heatmapLimit, setHeatmapLimit] = useState(15);
  // "Sales Rep — Period vs Prior Period Comparison" has its OWN independent
  // Period/Metric/Compare controls in the reference (rg-period/rg-metric/
  // rg-compare) — separate from the dashboard's global filter bar.
  const [rgPeriod, setRgPeriod] = useState('ytd');
  const [rgMetric, setRgMetric] = useState('arr');
  const [rgCompare, setRgCompare] = useState('prior_year');
  const [rgCustomFrom, setRgCustomFrom] = useState('');
  const [rgCustomTo, setRgCustomTo] = useState('');

  const changeRepComparisonPeriod = (event) => {
    const value = event.currentTarget.value;
    setRgPeriod(value);
    if (value === 'custom') {
      const anchor = latestMonth || data.period?.to || '';
      const year = anchor.slice(0, 4);
      setRgCustomFrom(current => current || (year ? `${year}-01` : ''));
      setRgCustomTo(current => current || anchor);
    }
  };

  const records = data.records || EMPTY_ARRAY;
  const trend = data.trend || EMPTY_ARRAY;
  // Global filter bar's Period selection (YTD/QTD/LTM/Custom) — threaded
  // through so this tab's "period" calculations (waterfall, rep leaderboard,
  // period comparison, new/expansion lists, logo period stats) match
  // whatever the user picked, instead of always assuming calendar-YTD.
  const periodFrom = data.period?.from || '';

  const latestMonth = useMemo(() => {
    if (data.period?.to) return data.period.to;
    const months = new Set(trend.map(row => row.month).filter(Boolean));
    records.forEach(row => {
      Object.entries(row.monthly_arr || {}).forEach(([month, value]) => {
        if (Number(value) > 0) months.add(month);
      });
    });
    return [...months].sort().at(-1) || '';
  }, [records, trend, data.period?.to]);

  const activeSummary = useMemo(() => {
    // "Prior period" tracks whatever date range the filter bar has selected
    // (YTD/QTD/LTM/Custom) — comparing against periodFrom, not always just
    // last calendar month, so the logo delta scales with the chosen filter.
    const prevMonth = periodFrom || getPrevMonth(latestMonth);
    const customers = {};
    records.forEach(row => {
      const latestArr = rowMonthArr(row, latestMonth);
      const customer = row.end_user || row.bill_to || '';
      if (latestArr > 0 && customer) {
        customers[customer] = (customers[customer] || 0) + latestArr;
      }
    });

    const customerRows = Object.entries(customers)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => Number(b.value) - Number(a.value));
    const totalARR = customerRows.reduce((sum, row) => sum + Number(row.value), 0);

    // Active Logo Count and ACV use month-end snapshots: distinct customers
    // with positive ARR in the latest month versus the prior month.
    const prevCustomerSet = new Set();
    records.forEach(row => {
      const customer = row.end_user || row.bill_to || '';
      if (rowMonthArr(row, prevMonth) > 0 && customer) prevCustomerSet.add(customer);
    });
    const apiActiveCustomers = Number(data.kpis?.customers);
    const activeCustomers = Number.isFinite(apiActiveCustomers)
      ? apiActiveCustomers
      : customerRows.length;
    const prevActiveCustomers = prevCustomerSet.size;

    const top = n => customerRows.slice(0, n).reduce((sum, row) => sum + Number(row.value), 0);
    const concentration = [1, 3, 5, 10].map(n => {
      const value = top(n);
      return {
        label: `Top ${n}`,
        value,
        percent: totalARR > 0 ? Number(((value / totalARR) * 100).toFixed(1)) : 0,
      };
    });
    const top10Pct = concentration.at(-1)?.percent || 0;
    concentration.push({
      label: 'Rest',
      // Reference's tooltip for the "Rest" bar sums sorted.slice(0,999) —
      // since 999 exceeds the customer count, that's the FULL array, i.e.
      // total ARR, not the true remainder after top 10. Almost certainly a
      // reference bug (should've been slice(10)), but we match it exactly.
      value: totalARR,
      percent: Number(Math.max(0, 100 - top10Pct).toFixed(1)),
    });

    return {
      latestMonth,
      prevMonth,
      totalARR,
      activeCustomers,
      logoDelta: activeCustomers - prevActiveCustomers,
      acv: activeCustomers > 0 ? totalARR / activeCustomers : 0,
      customerRows,
      concentration,
    };
  }, [records, latestMonth, periodFrom, data.kpis?.customers]);

  const acv = activeSummary.acv;

  const annualWaterfall = useMemo(() => {
    if (!latestMonth) return { rows: [], min: -5000000, max: 30000000, ticks: [], firstMonth: '', months: EMPTY_ARRAY };
    const year = latestMonth.slice(0, 4);
    const firstMonth = periodFrom || `${year}-01`;
    const months = monthRange(firstMonth, latestMonth);

    let startARR = 0;
    let endARR = 0;
    const totals = { new: 0, upsell: 0, churn: 0, downsell: 0 };
    records.forEach(row => {
      startARR += rowMonthArr(row, firstMonth);
      endARR += rowMonthArr(row, latestMonth);
      months.forEach(month => {
        const changes = row.monthly_changes || {};
        totals.new += Math.max(0, changeValue(changes, month, 'new'));
        totals.upsell += Math.max(0, changeValue(changes, month, 'upsell'));
        totals.churn += changeValue(changes, month, 'churn');
        totals.downsell += changeValue(changes, month, 'downsell');
      });
    });

    const rows = [
      { label: 'Start ARR', value: startARR, color: '#3d79b8', border: '#1e5fa8' },
      { label: 'New Logos', value: totals.new, color: '#37965d', border: '#15803d' },
      { label: 'Expansion', value: totals.upsell, color: '#42cf79', border: '#22c55e' },
      { label: 'Churn', value: totals.churn, color: '#c73f3f', border: '#b91c1c' },
      { label: 'Downsell', value: totals.downsell, color: '#f26f34', border: '#ea580c' },
      { label: 'End ARR', value: endARR, color: '#3d79b8', border: '#1e5fa8' },
    ];
    const maxValue = Math.max(...rows.map(row => Number(row.value || 0)), 1);
    const minValue = Math.min(...rows.map(row => Number(row.value || 0)), 0);
    const step = 5000000;
    const max = Math.max(step, Math.ceil(maxValue / step) * step);
    const min = Math.min(-step, Math.floor(minValue / step) * step);
    const ticks = [];
    for (let tick = max; tick >= min; tick -= step) ticks.push(tick);
    return { rows, min, max, ticks, firstMonth, months };
  }, [records, latestMonth, periodFrom]);

  const repLeaderboardRows = useMemo(() => {
    if (!latestMonth) return { rows: [], totals: { arr: 0, won: 0, churn: 0 } };
    const prevMonth = getPrevMonth(latestMonth);
    const year = latestMonth.slice(0, 4);
    const months = monthRange(periodFrom || `${year}-01`, latestMonth);

    const repData = {};
    records.forEach(row => {
      const rep = row.sales_person || row.sales_rep || 'Unknown';
      if (!repData[rep]) repData[rep] = { rep, arr: 0, prev: 0, won: 0, churn: 0, dn: 0, up: 0 };
      repData[rep].arr += rowMonthArr(row, latestMonth);
      repData[rep].prev += rowMonthArr(row, prevMonth);
      months.forEach(month => {
        const changes = row.monthly_changes || {};
        const newVal = changeValue(changes, month, 'new');
        const upsellVal = changeValue(changes, month, 'upsell');
        const churnVal = changeValue(changes, month, 'churn');
        const downsellVal = changeValue(changes, month, 'downsell');
        repData[rep].won += Math.max(0, newVal) + Math.max(0, upsellVal);
        repData[rep].churn += churnVal < 0 ? Math.abs(churnVal) : 0;
        repData[rep].dn += downsellVal < 0 ? Math.abs(downsellVal) : 0;
        repData[rep].up += Math.max(0, upsellVal);
      });
    });

    const rows = Object.values(repData)
      .filter(row => Number(row.arr) > 0 || Number(row.won) > 0)
      .map(row => ({
        ...row,
        // Displayed/sorted "Churn + Downsell" column — kept separate from the
        // raw churn/dn fields above, which the NRR formula below still needs
        // individually (NRR nets each against upsell the same way, but they
        // aren't otherwise interchangeable).
        churnTotal: Number(row.churn || 0) + Number(row.dn || 0),
        nrr: Number(row.prev) > 0 ? ((Number(row.prev) - Number(row.churn) - Number(row.dn) + Number(row.up)) / Number(row.prev)) * 100 : null,
      }));
    const sortedRows = [...rows].sort((a, b) => {
      const key = leaderSort.key;
      if (key === 'rep') {
        return leaderSort.asc ? a.rep.localeCompare(b.rep) : b.rep.localeCompare(a.rep);
      }
      const av = Number(a[key] ?? -Infinity);
      const bv = Number(b[key] ?? -Infinity);
      return leaderSort.asc ? av - bv : bv - av;
    });
    const totals = rows.reduce((acc, row) => ({
      arr: acc.arr + Number(row.arr || 0),
      won: acc.won + Number(row.won || 0),
      churnTotal: acc.churnTotal + Number(row.churnTotal || 0),
    }), { arr: 0, won: 0, churnTotal: 0 });
    return { rows: sortedRows, totals };
  }, [records, latestMonth, leaderSort, periodFrom]);

  const repPeriodComparison = useMemo(() => {
    if (!latestMonth) {
      return { currFrom: '', currTo: '', prevFrom: '', prevTo: '', rows: [], totals: { curr: 0, prev: 0, delta: 0, pct: 0 }, maxCurr: 1 };
    }
    // This card's Period selector is independent of the dashboard's global
    // filter bar (matches the reference's separate rg-period control) —
    // always anchored to the true latest month, not periodFrom.
    const year = latestMonth.slice(0, 4);
    const mo = Number(latestMonth.slice(5, 7));
    let currFrom;
    let currTo = latestMonth;
    if (rgPeriod === 'qtd') {
      const qStart = Math.floor((mo - 1) / 3) * 3 + 1;
      currFrom = `${year}-${String(qStart).padStart(2, '0')}`;
    } else if (rgPeriod === 'ltm') {
      currFrom = addMonths(latestMonth, -11);
    } else if (rgPeriod === 'custom') {
      currFrom = rgCustomFrom || `${year}-01`;
      currTo = rgCustomTo || latestMonth;
    } else {
      currFrom = `${year}-01`;
    }

    let prevFrom;
    let prevTo;
    if (rgCompare === 'prior_period') {
      const len = monthRange(currFrom, currTo).length || 1;
      prevTo = addMonths(currFrom, -1);
      prevFrom = addMonths(prevTo, -(len - 1));
    } else {
      prevFrom = addMonths(currFrom, -12);
      prevTo = addMonths(currTo, -12);
    }

    const calcRep = (from, to) => {
      const months = monthRange(from, to);
      const repData = {};
      records.forEach(row => {
        const rep = row.sales_person || row.sales_rep || 'Unknown';
        if (!repData[rep]) repData[rep] = { arr: 0, won: 0, churn: 0 };
        months.forEach(month => {
          // ARR is averaged across the selected month-end snapshots. Using
          // only the final month made YTD/QTD/LTM identical because every
          // range ends in the same latest month.
          repData[rep].arr += rowMonthArr(row, month);
          const changes = row.monthly_changes || {};
          const newVal = changeValue(changes, month, 'new');
          const upsellVal = changeValue(changes, month, 'upsell');
          const churnVal = changeValue(changes, month, 'churn');
          const downVal = changeValue(changes, month, 'downsell');
          repData[rep].won += Math.max(0, newVal) + Math.max(0, upsellVal);
          repData[rep].churn += (churnVal < 0 ? Math.abs(churnVal) : 0) + (downVal < 0 ? Math.abs(downVal) : 0);
        });
      });
      const monthCount = months.length || 1;
      Object.values(repData).forEach(rep => {
        rep.arr /= monthCount;
      });
      return repData;
    };

    const currData = calcRep(currFrom, currTo);
    const prevData = calcRep(prevFrom, prevTo);
    const reps = [...new Set([...Object.keys(currData), ...Object.keys(prevData)])].filter(Boolean);
    const rows = reps.map(rep => {
      const curr = Number(currData[rep]?.[rgMetric] || 0);
      const prev = Number(prevData[rep]?.[rgMetric] || 0);
      const delta = curr - prev;
      const pct = prev > 0 ? (delta / prev) * 100 : (curr > 0 ? 100 : 0);
      return { rep, curr, prev, delta, pct };
    }).filter(row => row.curr > 0 || row.prev > 0).sort((a, b) => b.curr - a.curr);
    const totals = rows.reduce((acc, row) => ({
      curr: acc.curr + row.curr,
      prev: acc.prev + row.prev,
      delta: acc.delta + row.delta,
      pct: 0,
    }), { curr: 0, prev: 0, delta: 0, pct: 0 });
    totals.pct = totals.prev > 0 ? (totals.delta / totals.prev) * 100 : 0;
    return {
      currFrom,
      currTo,
      prevFrom,
      prevTo,
      rows,
      totals,
      maxCurr: Math.max(1, ...rows.map(row => Math.max(row.curr, row.prev))),
    };
  }, [records, latestMonth, rgPeriod, rgMetric, rgCompare, rgCustomFrom, rgCustomTo]);

  const customerHealthHeatmap = useMemo(() => {
    const monthTotals = {};
    records.forEach(row => {
      Object.entries(row.monthly_arr || {}).forEach(([month, value]) => {
        monthTotals[month] = (monthTotals[month] || 0) + Number(value || 0);
      });
    });
    const last18 = Object.keys(monthTotals)
      .filter(month => Number(monthTotals[month] || 0) > 0)
      .sort()
      .slice(-18);
    const latest = last18.at(-1) || latestMonth;
    const customers = {};
    records.forEach(row => {
      const customer = row.end_user || row.bill_to || 'Unspecified';
      if (!customers[customer]) customers[customer] = {};
      last18.forEach(month => {
        customers[customer][month] = (customers[customer][month] || 0) + rowMonthArr(row, month);
      });
    });
    const rows = Object.entries(customers)
      .filter(([, months]) => Number(months[latest] || 0) > 0)
      .sort((a, b) => Number(b[1][latest] || 0) - Number(a[1][latest] || 0))
      .slice(0, heatmapLimit)
      .map(([customer, months]) => ({
        customer,
        shortName: customer.length > 20 ? `${customer.slice(0, 19)}...` : customer,
        cells: last18.map((month, index) => {
          const value = Number(months[month] || 0);
          const prev = index > 0 ? Number(months[last18[index - 1]] || 0) : value;
          const status = value <= 0 ? 'zero'
            : prev <= 0 ? 'same'
            : value > prev * 1.01 ? 'up'
            : value < prev * 0.99 ? 'down'
            : 'same';
          return { month, value, status };
        }),
      }));
    return { months: last18, rows };
  }, [records, latestMonth, heatmapLimit]);

  const customerActionLists = useMemo(() => {
    const monthTotals = {};
    records.forEach(row => {
      Object.entries(row.monthly_arr || {}).forEach(([month, value]) => {
        monthTotals[month] = (monthTotals[month] || 0) + Number(value || 0);
      });
    });
    const allActiveMonths = Object.keys(monthTotals)
      .filter(month => Number(monthTotals[month] || 0) > 0)
      .sort();
    const last6 = allActiveMonths.slice(-6);
    const latest = last6.at(-1) || latestMonth;
    const year = latest ? latest.slice(0, 4) : '';
    const periodMonths = latest ? monthRange(periodFrom || `${year}-01`, latest) : [];

    const customerData = {};
    records.forEach(row => {
      const customer = row.end_user || row.bill_to || 'Unspecified';
      if (!customerData[customer]) customerData[customer] = { months: {}, rep: row.sales_person || row.sales_rep || '-' };
      if (!customerData[customer].rep || customerData[customer].rep === '-') customerData[customer].rep = row.sales_person || row.sales_rep || '-';
      last6.forEach(month => {
        customerData[customer].months[month] = (customerData[customer].months[month] || 0) + rowMonthArr(row, month);
      });
    });

    const atRisk = Object.entries(customerData).map(([customer, info]) => {
      const values = last6.map(month => Number(info.months[month] || 0));
      let consecutive = 0;
      for (let i = values.length - 1; i > 0; i -= 1) {
        if (values[i] < values[i - 1] && values[i] > 0) consecutive += 1;
        else break;
      }
      if (consecutive < 2) return null;
      const current = values.at(-1) || 0;
      const peak = values[values.length - 1 - consecutive] || 0;
      const decline = current - peak;
      const risk = decline < -peak * 0.3 ? 'high' : decline < -peak * 0.1 ? 'med' : 'low';
      return { customer, rep: info.rep, current, decline, consecutive, risk };
    }).filter(Boolean).sort((a, b) => a.decline - b.decline).slice(0, 15);

    const newLogoMap = {};
    records.forEach(row => {
      periodMonths.forEach(month => {
        const newVal = changeValue(row.monthly_changes || {}, month, 'new');
        if (newVal <= 0) return;
        const customer = row.end_user || row.bill_to || 'Unspecified';
        const product = row.product_l2 || row.sub_product || row.product_type || row.product || '-';
        const key = `${customer}||${product}`;
        if (!newLogoMap[key]) {
          newLogoMap[key] = { customer, product, rep: row.sales_person || row.sales_rep || '-', arr: 0, since: month };
        }
        newLogoMap[key].arr += newVal;
        if (month < newLogoMap[key].since) newLogoMap[key].since = month;
      });
    });
    const newLogos = Object.values(newLogoMap).sort((a, b) => Number(b.arr) - Number(a.arr));

    const expansionMap = {};
    records.forEach(row => {
      const customer = row.end_user || row.bill_to || 'Unspecified';
      if (!expansionMap[customer]) {
        expansionMap[customer] = { customer, rep: row.sales_person || '', first: '', last: '', arr: 0, hasUpsell: false };
      }
      const info = expansionMap[customer];
      if (!info.rep) info.rep = row.sales_person || '';
      const activeMonths = Object.entries(row.monthly_arr || {})
        .filter(([, value]) => Number(value || 0) > 0)
        .map(([month]) => month)
        .sort();
      if (activeMonths.length) {
        if (!info.first || activeMonths[0] < info.first) info.first = activeMonths[0];
        if (!info.last || activeMonths.at(-1) > info.last) info.last = activeMonths.at(-1);
      }
      info.arr += rowMonthArr(row, latest);
      Object.values(row.monthly_changes || {}).forEach(change => {
        if (Number(change?.Upsell || change?.upsell || 0) > 0) info.hasUpsell = true;
      });
    });
    const latestIndex = allActiveMonths.indexOf(latest);
    const expansion = Object.values(expansionMap).filter(info => {
      if (!info.first || Number(info.arr) <= 0 || info.hasUpsell) return false;
      const monthsActive = latestIndex - allActiveMonths.indexOf(info.first);
      return monthsActive >= 12;
    }).map(info => ({
      ...info,
      months: latestIndex - allActiveMonths.indexOf(info.first),
    })).sort((a, b) => Number(b.arr) - Number(a.arr)).slice(0, 15);

    return { atRisk, newLogos, expansion };
  }, [records, latestMonth, periodFrom]);

  const updateLeaderSort = (key) => {
    setLeaderSort(current => ({ key, asc: current.key === key ? !current.asc : false }));
  };

  // ── T2: ACV — Average Contract Value (matches reference openDetailModal('t2:acv')) ──
  const acvDetail = useMemo(() => {
    const latest = activeSummary.latestMonth;
    const prev = activeSummary.prevMonth;

    const custACV = {};
    records.forEach(row => {
      const v = rowMonthArr(row, latest);
      const customer = row.end_user || row.bill_to || '';
      if (v > 0 && customer) custACV[customer] = (custACV[customer] || 0) + v;
    });
    const custList = Object.entries(custACV).sort((a, b) => b[1] - a[1]);
    const totalARR = custList.reduce((sum, [, v]) => sum + v, 0);
    // Use the same latest-month snapshot count as the Active Logo card.
    const logos = activeSummary.activeCustomers;
    const acvVal = logos > 0 ? totalARR / logos : 0;

    const regACV = {};
    const regLogos = {};
    records.forEach(row => {
      const v = rowMonthArr(row, latest);
      if (v <= 0) return;
      const region = row.business_unit || 'Unspecified';
      const customer = row.end_user || row.bill_to || 'Unspecified';
      regACV[region] = (regACV[region] || 0) + v;
      if (!regLogos[region]) regLogos[region] = {};
      regLogos[region][customer] = 1;
    });
    const regACVList = Object.entries(regACV).map(([label, sum]) => {
      const n = Object.keys(regLogos[label] || {}).length || 1;
      return [label, sum / n, n];
    }).sort((a, b) => b[1] - a[1]);

    const prodACV = {};
    const prodLogos = {};
    records.forEach(row => {
      const v = rowMonthArr(row, latest);
      const product = row.product_l2 || row.product_type || '';
      if (v <= 0 || !product) return;
      const customer = row.end_user || row.bill_to || 'Unspecified';
      prodACV[product] = (prodACV[product] || 0) + v;
      if (!prodLogos[product]) prodLogos[product] = {};
      prodLogos[product][customer] = 1;
    });
    const prodACVList = Object.entries(prodACV).map(([label, sum]) => {
      const n = Object.keys(prodLogos[label] || {}).length || 1;
      return [label, sum / n];
    }).sort((a, b) => b[1] - a[1]).slice(0, 6);

    const prevCustSet = {};
    records.forEach(row => {
      const customer = row.end_user || row.bill_to || '';
      if (rowMonthArr(row, prev) > 0 && customer) prevCustSet[customer] = 1;
    });
    const prevLogos = Object.keys(prevCustSet).length;
    let prevTotalARR = 0;
    records.forEach(row => { prevTotalARR += rowMonthArr(row, prev); });
    const prevACV = prevLogos > 0 ? prevTotalARR / prevLogos : 0;

    const bands = { '<$10K': 0, '$10K–$50K': 0, '$50K–$200K': 0, '$200K–$1M': 0, '>$1M': 0 };
    custList.forEach(([, v]) => {
      if (v < 10000) bands['<$10K'] += 1;
      else if (v < 50000) bands['$10K–$50K'] += 1;
      else if (v < 200000) bands['$50K–$200K'] += 1;
      else if (v < 1000000) bands['$200K–$1M'] += 1;
      else bands['>$1M'] += 1;
    });
    return {
      title: 'Average Contract Value (ACV)',
      formula: `Total ARR ÷ ${logos} customers active as of ${latest}`,
      big: fF(acvVal),
      color: '#0f766e',
      stats: [
        { label: 'Current ACV', value: fF(acvVal), color: '#0f766e' },
        { label: 'ACV at Period Start', value: fF(prevACV), color: '#718096' },
        { label: 'ACV Change', value: `${acvVal - prevACV >= 0 ? '+' : ''}${fF(acvVal - prevACV)}`, color: acvVal >= prevACV ? '#15803d' : '#b91c1c' },
        { label: 'Total ARR', value: fF(totalARR), color: '#1e5fa8' },
        { label: 'Active Customers', value: `${logos}`, color: '#6d28d9' },
        { label: 'Highest ACV Cust', value: custList.length ? `${custList[0][0].split(' ')[0]}…` : '—', color: '#0f766e' },
      ],
      // Percentages are relative to this card's own totals, not the largest
      // row shown — per explicit request. "Highest ACV Customers" is % of
      // Total ARR; the BU/Product sections show AVERAGES (not sums), so
      // there's no "total" to divide a sum into — they're shown as % of the
      // overall blended ACV instead (can exceed 100% for above-average
      // groups); the distribution band counts are % of Active Customers.
      sections: [
        {
          title: '👤 Highest ACV Customers (top ARR per account)',
          items: custList.slice(0, 6).map(([label, value]) => ({
            label, value: fF(value), percent: totalARR > 0 ? (value / totalARR) * 100 : 0, color: '#0f766e',
          })),
        },
        {
          title: '🌍 ACV by BU (Avg ARR per account)',
          items: regACVList.map(([label, value, n]) => ({
            label: `${label} (${n} accts)`, value: fF(value),
            percent: acvVal > 0 ? (value / acvVal) * 100 : 0,
            color: REG_COLORS[label] || '#64748b',
          })),
        },
        {
          title: '📦 ACV by Product (Avg ARR per account)',
          color: '#6d28d9',
          items: prodACVList.map(([label, value]) => ({
            label, value: fF(value), percent: acvVal > 0 ? (value / acvVal) * 100 : 0, color: '#6d28d9',
          })),
        },
        {
          // Account counts, not money — see note on the Logos-by-BU section below.
          title: '📊 Customer ACV Distribution (# of accounts)',
          color: '#0f766e',
          items: Object.entries(bands).map(([label, value]) => ({
            label, value: `${value}`, percent: logos > 0 ? (value / logos) * 100 : 0, color: '#0f766e',
          })),
        },
      ],
    };
  }, [records, activeSummary.latestMonth, activeSummary.prevMonth, activeSummary.activeCustomers]);

  // ── T2: LOGOS — Active Customer Count (matches reference openDetailModal('t2:logos')) ──
  const logosDetail = useMemo(() => {
    const latest = activeSummary.latestMonth;
    const prev = activeSummary.prevMonth;

    const year = latest ? latest.slice(0, 4) : '';
    const periodMonths = latest ? monthRange(periodFrom || `${year}-01`, latest) : [];

    // Headline and comparison both use month-end snapshots.
    const logos = activeSummary.activeCustomers;

    const custSet = {};
    const prevCustSet = {};
    records.forEach(row => {
      const customer = row.end_user || row.bill_to || '';
      if (!customer) return;
      if (rowMonthArr(row, latest) > 0) custSet[customer] = 1;
      if (rowMonthArr(row, prev) > 0) prevCustSet[customer] = 1;
    });
    // Net Logo Change compares the current and prior month snapshots.
    const logosSnapshot = Object.keys(custSet).length;
    const prevLogos = Object.keys(prevCustSet).length;
    let gained = 0;
    let lost = 0;
    Object.keys(custSet).forEach(c => { if (!prevCustSet[c]) gained += 1; });
    Object.keys(prevCustSet).forEach(c => { if (!custSet[c]) lost += 1; });

    // BU and sales-rep breakdowns use the same latest-month snapshot.
    const regLogos = {};
    records.forEach(row => {
      if (rowMonthArr(row, latest) > 0) {
        const region = row.business_unit || 'Unspecified';
        const customer = row.end_user || row.bill_to || 'Unspecified';
        if (!regLogos[region]) regLogos[region] = {};
        regLogos[region][customer] = 1;
      }
    });
    const regL = Object.entries(regLogos).map(([label, set]) => [label, Object.keys(set).length]).sort((a, b) => b[1] - a[1]);

    const repLogos = {};
    records.forEach(row => {
      if (rowMonthArr(row, latest) > 0) {
        const rep = row.sales_person || 'Unassigned';
        const customer = row.end_user || row.bill_to || 'Unspecified';
        if (!repLogos[rep]) repLogos[rep] = {};
        repLogos[rep][customer] = 1;
      }
    });
    const repL = Object.entries(repLogos).map(([label, set]) => [label, Object.keys(set).length]).sort((a, b) => b[1] - a[1]).slice(0, 6);

    const newLogoMap = {};
    records.forEach(row => {
      const customer = row.end_user || row.bill_to || 'Unspecified';
      periodMonths.forEach(month => {
        const newVal = changeValue(row.monthly_changes || {}, month, 'new');
        if (newVal > 0) newLogoMap[customer] = (newLogoMap[customer] || 0) + newVal;
      });
    });
    const newLogoL = Object.entries(newLogoMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

    const churnLogoMap = {};
    records.forEach(row => {
      const customer = row.end_user || row.bill_to || 'Unspecified';
      periodMonths.forEach(month => {
        const churnVal = changeValue(row.monthly_changes || {}, month, 'churn');
        if (churnVal < 0) churnLogoMap[customer] = (churnLogoMap[customer] || 0) + Math.abs(churnVal);
      });
    });
    const churnLogoL = Object.entries(churnLogoMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

    const newLogoTotal = Object.values(newLogoMap).reduce((sum, v) => sum + v, 0);
    const churnLogoTotal = Object.values(churnLogoMap).reduce((sum, v) => sum + v, 0);

    return {
      title: 'Active Logo Count',
      formula: `${logos} customers active as of ${latest}`,
      big: `${logos}`,
      color: '#1d4ed8',
      stats: [
        { label: 'Active Logos Now', value: `${logos}`, color: '#1d4ed8' },
        { label: 'Active at Period Start', value: `${prevLogos}`, color: '#718096' },
        { label: 'Net Logo Change', value: `${logosSnapshot - prevLogos >= 0 ? '+' : ''}${logosSnapshot - prevLogos}`, color: logosSnapshot >= prevLogos ? '#15803d' : '#b91c1c' },
        { label: 'New This Period', value: `${gained} added`, color: '#15803d' },
        { label: 'Shrinkage This Period', value: `${lost} churned`, color: '#b91c1c' },
        { label: 'Retention Rate', value: prevLogos > 0 ? `${(((prevLogos - lost) / prevLogos) * 100).toFixed(1)}%` : '—', color: '#0f766e' },
      ],
      // Percentages are relative to this card's own totals, not the largest
      // row shown — per explicit request: BU/Rep counts are % of Active
      // Customers (logos); New/Churned Logos are % of their own ARR total.
      sections: [
        {
          // Account counts, not money — the reference's barRow() blanket-
          // formats every bar value through fF() (its $ money formatter),
          // even these counts, but that reads as a bug there, not something
          // to replicate: show the plain count instead.
          title: '🌍 Logos by BU',
          items: regL.map(([label, value]) => ({
            label: `${label} region`, value: `${value}`, percent: logos > 0 ? (value / logos) * 100 : 0, color: REG_COLORS[label] || '#64748b',
          })),
        },
        {
          title: '🧑‍💼 Logos by Sales Rep (# of accounts managed)',
          color: '#0f766e',
          items: repL.map(([label, value]) => ({
            label, value: `${value}`, percent: logos > 0 ? (value / logos) * 100 : 0, color: '#0f766e',
          })),
        },
        {
          title: '🆕 New Logos This Period (by New ARR)',
          color: '#15803d',
          emptyText: 'None this period',
          items: newLogoL.map(([label, value]) => ({
            label, value: fF(value), percent: newLogoTotal > 0 ? (value / newLogoTotal) * 100 : 0, color: '#15803d',
          })),
        },
        {
          title: '🔴 Churned Logos This Period',
          color: '#b91c1c',
          emptyText: '✅ No churned logos this period',
          emptyColor: '#15803d',
          items: churnLogoL.map(([label, value]) => ({
            label, value: `−${fF(value)}`, percent: churnLogoTotal > 0 ? (value / churnLogoTotal) * 100 : 0, color: '#b91c1c',
          })),
        },
      ],
    };
  }, [records, activeSummary.latestMonth, activeSummary.prevMonth, activeSummary.activeCustomers, periodFrom]);

  const openModal = (type) => {
    if (type === 'acv') setModal(acvDetail);
    else if (type === 'logos') setModal(logosDetail);
  };

  // "Rest" bar deliberately shows the reference's total-ARR quirk (see the
  // comment on activeSummary.concentration above) — the drill-down list
  // below shows the real remaining customers regardless, since that's what's
  // actually useful to click into.
  const openConcentrationTier = (tier, index) => {
    const n = [1, 3, 5, 10][index];
    const list = n ? activeSummary.customerRows.slice(0, n) : activeSummary.customerRows.slice(10);
    const totalARR = activeSummary.totalARR;
    const tierTotal = list.reduce((sum, row) => sum + Number(row.value), 0);
    setModal({
      title: `${tier.label} Customers by ARR`,
      subtitle: `${list.length} customer${list.length === 1 ? '' : 's'} as of ${activeSummary.latestMonth}`,
      big: fF(tierTotal),
      color: '#1e5fa8',
      stats: [
        { label: 'Customers in Tier', value: `${list.length}`, color: '#1e5fa8' },
        { label: 'Tier ARR', value: fF(tierTotal), color: '#15803d' },
        { label: '% of Total ARR', value: totalARR > 0 ? `${((tierTotal / totalARR) * 100).toFixed(1)}%` : '—', color: '#7c3aed' },
        { label: 'Total ARR (All Customers)', value: fF(totalARR), color: '#0f766e' },
      ],
      sections: [
        {
          title: `👤 ${tier.label} Customers`,
          items: list.map(row => ({
            label: row.label,
            value: fF(row.value),
            percent: totalARR > 0 ? (Number(row.value) / totalARR) * 100 : 0,
          })),
        },
      ],
    });
  };

  const openAnnualWaterfallBar = (row) => {
    const { firstMonth, months } = annualWaterfall;

    if (row.label === 'Start ARR' || row.label === 'End ARR') {
      const targetMonth = row.label === 'Start ARR' ? firstMonth : latestMonth;
      const custMap = {};
      records.forEach(r => {
        const v = rowMonthArr(r, targetMonth);
        if (v <= 0) return;
        const customer = r.end_user || r.bill_to || 'Unspecified';
        custMap[customer] = (custMap[customer] || 0) + v;
      });
      const list = Object.entries(custMap).sort((a, b) => b[1] - a[1]);
      const total = list.reduce((sum, [, v]) => sum + v, 0);
      setModal({
        title: `${row.label} — ${targetMonth}`,
        subtitle: `${list.length} customers · ${fF(total)} total ARR`,
        big: fF(total),
        color: row.border,
        stats: [
          { label: 'Customers', value: `${list.length}`, color: row.border },
          { label: 'Total ARR', value: fF(total), color: row.border },
        ],
        sections: [{
          title: `👤 ${row.label} — Customers`,
          items: list.slice(0, 30).map(([label, value]) => ({ label, value: fF(value), percent: total > 0 ? (value / total) * 100 : 0 })),
        }],
      });
      return;
    }

    const categoryKey = { 'New Logos': 'new', Expansion: 'upsell', Churn: 'churn', Downsell: 'downsell' }[row.label];
    const events = [];
    records.forEach(r => {
      const changes = r.monthly_changes || {};
      months.forEach(month => {
        const amount = changeValue(changes, month, categoryKey);
        if (!amount) return;
        if ((categoryKey === 'new' || categoryKey === 'upsell') && amount <= 0) return;
        if ((categoryKey === 'churn' || categoryKey === 'downsell') && amount >= 0) return;
        const customer = r.end_user || r.bill_to || 'Unspecified';
        const product = r.product_l2 || r.product_type || r.product || '-';
        events.push({ customer, product, month, amount });
      });
    });
    events.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    const total = events.reduce((sum, event) => sum + Math.abs(event.amount), 0);
    setModal({
      title: `${row.label} — FY${(latestMonth || '').slice(0, 4)} YTD`,
      subtitle: `${events.length} event${events.length === 1 ? '' : 's'} · ${fF(total)} total`,
      big: fF(Math.abs(row.value)),
      color: row.border,
      stats: [
        { label: 'Events', value: `${events.length}`, color: row.border },
        { label: `${row.label} Total`, value: fF(Math.abs(row.value)), color: row.border },
      ],
      sections: [{
        title: `📋 ${row.label} — Deal Detail${events.length > 30 ? ' (top 30 by size)' : ''}`,
        items: events.slice(0, 30).map(event => ({
          label: `${event.customer} · ${event.product} · ${event.month}`,
          value: fF(Math.abs(event.amount)),
          percent: total > 0 ? (Math.abs(event.amount) / total) * 100 : 0,
        })),
      }],
    });
  };

  return (
    <>
      <ArrDetailModal detail={modal} onClose={() => setModal(null)} />

      {/* â”€â”€â”€ 3 Header KPI Cards â”€â”€â”€ */}
      <div className="arr-t2-kpis">
        <DevOverlay name="BU ACV KPI">
          <div className="arr-t2kcard" onClick={() => openModal('acv')}>
            <div className="arr-t2kcard-label">Average Contract Value (ACV)</div>
            <div className="arr-t2kcard-val">{shortMoney(acv)}</div>
            <div className="arr-t2kcard-sub">{activeSummary.activeCustomers} customers active as of {activeSummary.latestMonth}</div>
          </div>
        </DevOverlay>
        <DevOverlay name="BU Logo Count KPI">
          <div className="arr-t2kcard arr-t2kcard-cyan" onClick={() => openModal('logos')}>
            <div className="arr-t2kcard-label">Active Logo Count</div>
            <div className="arr-t2kcard-val">{activeSummary.activeCustomers || '-'}</div>
            <div className="arr-t2kcard-sub">{activeSummary.logoDelta >= 0 ? '\u25B2 +' : '\u25BC '}{Math.abs(activeSummary.logoDelta)} vs prior period</div>
          </div>
        </DevOverlay>
      </div>

      <DevOverlay name="ARR Concentration Risk">
        <div className="arr-chart-card arr-concentration-card">
          <div className="arr-chart-title">ARR Concentration ↗</div>
          <div className="arr-chart-sub">What % of ARR comes from top 1, 3, 5, 10 customers · Click a bar to see those customers</div>
          <div className="arr-concentration-chart">
            <div className="arr-concentration-yaxis">
              {[100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0].map(tick => (
                <span key={tick}>{tick}%</span>
              ))}
            </div>
            <div className="arr-concentration-plot">
              {[100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0].map(tick => (
                <div className="arr-concentration-gridline" style={{ bottom: `${tick}%` }} key={tick} />
              ))}
              <div className="arr-concentration-bars">
                {activeSummary.concentration.map((tier, index) => (
                  <div
                    className="arr-concentration-bar-col"
                    key={tier.label}
                    role="button"
                    tabIndex="0"
                    style={{ cursor: 'pointer' }}
                    onClick={() => openConcentrationTier(tier, index)}
                    onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openConcentrationTier(tier, index); } }}
                  >
                    <div className="arr-concentration-bar-wrap">
                      <div
                        className="arr-concentration-bar"
                        style={{
                          height: `${Math.min(100, Math.max(0, tier.percent))}%`,
                          background: `${['#b91c1c', '#ea580c', '#d97706', '#1e5fa8', '#15803d'][index]}cc`,
                          borderColor: ['#b91c1c', '#ea580c', '#d97706', '#1e5fa8', '#15803d'][index],
                        }}
                        title={`${tier.percent}% of ARR | ${fmt(tier.value)} · Click for customers`}
                      />
                    </div>
                    <div className="arr-concentration-xlabel">{tier.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </DevOverlay>

      <DevOverlay name="Annual ARR Waterfall">
        <div className="arr-chart-card arr-annual-waterfall-card">
          <div className="arr-chart-title">Annual ARR Waterfall ↗</div>
          <div className="arr-chart-sub">Year-start → New → Expansion → Churn → Downsell → Year-end · Click a bar for the deal detail</div>
          <div className="arr-annual-waterfall-chart">
            <div className="arr-annual-yaxis">
              {annualWaterfall.ticks.map(tick => (
                <span key={tick}>{axisMoney(tick)}</span>
              ))}
            </div>
            <div className="arr-annual-plot">
              {annualWaterfall.ticks.map(tick => {
                const y = ((Number(tick) - annualWaterfall.min) / (annualWaterfall.max - annualWaterfall.min || 1)) * 100;
                return <div className="arr-annual-gridline" style={{ bottom: `${y}%` }} key={tick} />;
              })}
              <div
                className="arr-annual-zero-line"
                style={{ bottom: `${((0 - annualWaterfall.min) / (annualWaterfall.max - annualWaterfall.min || 1)) * 100}%` }}
              />
              <div className="arr-annual-bars">
                {annualWaterfall.rows.map(row => {
                  const range = annualWaterfall.max - annualWaterfall.min || 1;
                  const zeroPct = ((0 - annualWaterfall.min) / range) * 100;
                  const valuePct = ((Number(row.value || 0) - annualWaterfall.min) / range) * 100;
                  const isNegative = Number(row.value || 0) < 0;
                  return (
                    <div
                      className="arr-annual-bar-col"
                      key={row.label}
                      role="button"
                      tabIndex="0"
                      style={{ cursor: 'pointer' }}
                      onClick={() => openAnnualWaterfallBar(row)}
                      onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openAnnualWaterfallBar(row); } }}
                    >
                      <div className="arr-annual-bar-area">
                        <div
                          className={`arr-annual-bar${isNegative ? ' negative' : ''}`}
                          style={{
                            bottom: `${Math.min(zeroPct, valuePct)}%`,
                            height: `${Math.max(0.6, Math.abs(valuePct - zeroPct))}%`,
                            background: row.color,
                            borderColor: row.border,
                          }}
                          title={`${row.label}: ${axisMoney(row.value)} · Click for detail`}
                        />
                      </div>
                      <div className="arr-annual-xlabel">{row.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </DevOverlay>

      <DevOverlay name="Sales Rep Leaderboard">
        <div className="arr-chart-card arr-rep-leaderboard-card">
          <div className="arr-chart-title">Sales Rep Leaderboard</div>
          <div className="arr-chart-sub">Closing ARR · New Won + Expansion · Churn + Downsell attributed · NRR per rep</div>
          <div className="arr-rep-leaderboard-wrap">
            <table className="arr-rep-leaderboard-table">
              <thead>
                <tr>
                  <th onClick={() => updateLeaderSort('rep')}>Sales Rep</th>
                  <th onClick={() => updateLeaderSort('arr')}>Closing ARR ↕</th>
                  <th onClick={() => updateLeaderSort('won')}>New Won + Expansion ↕</th>
                  <th onClick={() => updateLeaderSort('churnTotal')}>Churn + Downsell ↕</th>
                  <th>NRR%</th>
                </tr>
              </thead>
              <tbody>
                {repLeaderboardRows.rows.map(row => {
                  const nrrColor = row.nrr == null || row.nrr < 100 ? '#b91c1c' : '#15803d';
                  return (
                    <tr key={row.rep}>
                      <td className="arr-rep-name">{row.rep}</td>
                      <td className="arr-rep-money arr-rep-arr">{fmtFull(row.arr)}</td>
                      <td className="arr-rep-money arr-rep-won">{row.won ? fmtFull(row.won) : '—'}</td>
                      <td className="arr-rep-money arr-rep-churn">{row.churnTotal ? fmtFull(row.churnTotal) : '—'}</td>
                      <td className="arr-rep-nrr" style={{ color: nrrColor }}>{row.nrr == null ? '—' : `${row.nrr.toFixed(1)}%`}</td>
                    </tr>
                  );
                })}
                {!repLeaderboardRows.rows.length && (
                  <tr><td colSpan={5} className="bm-empty-row">No sales rep data available.</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total ({repLeaderboardRows.rows.length} reps)</td>
                  <td>{fmtFull(repLeaderboardRows.totals.arr)}</td>
                  <td className="arr-rep-won">{fmtFull(repLeaderboardRows.totals.won)}</td>
                  <td className="arr-rep-churn">{fmtFull(repLeaderboardRows.totals.churnTotal)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </DevOverlay>

      <DevOverlay name="Sales Rep Period Comparison">
        <div className="arr-chart-card arr-rep-period-card">
          <div className="arr-rep-period-head">
            <div>
              <div className="arr-chart-title">Sales Rep — Period vs Prior Period Comparison</div>
              <div className="arr-chart-sub">
                {rgMetric === 'arr' ? 'Closing ARR' : rgMetric === 'won' ? 'New Won' : 'Churn'} | {repPeriodComparison.currFrom} to {repPeriodComparison.currTo} vs {repPeriodComparison.prevFrom} to {repPeriodComparison.prevTo}
              </div>
            </div>
            <div className="arr-rep-period-controls" onClick={event => event.stopPropagation()}>
              <span>PERIOD:</span>
              <select aria-label="Comparison period" value={rgPeriod} onChange={changeRepComparisonPeriod}>
                <option value="ytd">YTD</option>
                <option value="qtd">QTD</option>
                <option value="ltm">LTM</option>
                <option value="custom">Custom</option>
              </select>
              {rgPeriod === 'custom' && (
                <>
                  <input type="month" aria-label="Comparison period from" className="dash-filter-month" value={rgCustomFrom} onChange={event => setRgCustomFrom(event.currentTarget.value)} />
                  <span>—</span>
                  <input type="month" aria-label="Comparison period to" className="dash-filter-month" value={rgCustomTo} onChange={event => setRgCustomTo(event.currentTarget.value)} />
                </>
              )}
              <span>METRIC:</span>
              <select aria-label="Comparison metric" value={rgMetric} onChange={event => setRgMetric(event.currentTarget.value)}>
                <option value="arr">Closing ARR</option>
                <option value="won">New Won</option>
                <option value="churn">Churn</option>
              </select>
              <span>VS:</span>
              <select aria-label="Compare against" value={rgCompare} onChange={event => setRgCompare(event.currentTarget.value)}>
                <option value="prior_year">Same Period Last Year</option>
                <option value="prior_period">Previous Period</option>
              </select>
            </div>
          </div>
          <div className="arr-rep-period-wrap">
            <table className="arr-rep-period-table" key={`${rgPeriod}-${rgMetric}-${rgCompare}-${rgCustomFrom}-${rgCustomTo}`}>
              <thead>
                <tr>
                  <th>Sales Rep</th>
                  <th>Current Period</th>
                  <th>{rgCompare === 'prior_period' ? 'Previous Period' : 'Same Period Last Year'}</th>
                  <th>Change ($)</th>
                  <th>Growth %</th>
                  <th>Trend</th>
                </tr>
              </thead>
              <tbody>
                {repPeriodComparison.rows.map(row => {
                  const pctColor = row.pct >= 10 ? '#15803d' : row.pct >= 0 ? '#d97706' : '#b91c1c';
                  const pctBg = row.pct >= 10 ? '#dcfce7' : row.pct >= 0 ? '#fef9c3' : '#fee2e2';
                  const deltaColor = row.delta >= 0 ? '#15803d' : '#b91c1c';
                  const nowWidth = repPeriodComparison.maxCurr > 0 ? Math.round((row.curr / repPeriodComparison.maxCurr) * 100) : 0;
                  const prevWidth = repPeriodComparison.maxCurr > 0 ? Math.round((row.prev / repPeriodComparison.maxCurr) * 100) : 0;
                  return (
                    <tr key={row.rep}>
                      <td className="arr-rep-period-name">{row.rep}</td>
                      <td className="arr-rep-period-money current">{fmtFull(row.curr)}</td>
                      <td className="arr-rep-period-money previous">{fmtFull(row.prev)}</td>
                      <td className="arr-rep-period-money" style={{ color: deltaColor }}>{row.delta >= 0 ? '+' : ''}{fmtFull(row.delta)}</td>
                      <td className="arr-rep-period-growth"><span style={{ background: pctBg, color: pctColor }}>{row.pct >= 0 ? '+' : ''}{row.pct.toFixed(1)}%</span></td>
                      <td>
                        <div className="arr-rep-period-bars">
                          <div className="arr-rep-period-bar-row">
                            <span>Now</span>
                            <i><b style={{ width: `${nowWidth}%`, background: '#1e5fa8' }} /></i>
                          </div>
                          <div className="arr-rep-period-bar-row">
                            <span>Prev</span>
                            <i><b style={{ width: `${prevWidth}%`, background: '#94a3b8' }} /></i>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!repPeriodComparison.rows.length && (
                  <tr><td colSpan={6} className="bm-empty-row">No sales rep comparison data available.</td></tr>
                )}
                {!!repPeriodComparison.rows.length && (
                  <tr className="arr-rep-period-total">
                    <td>Total ({repPeriodComparison.rows.length} reps)</td>
                    <td>{fmtFull(repPeriodComparison.totals.curr)}</td>
                    <td>{fmtFull(repPeriodComparison.totals.prev)}</td>
                    <td style={{ color: repPeriodComparison.totals.delta >= 0 ? '#15803d' : '#b91c1c' }}>{repPeriodComparison.totals.delta >= 0 ? '+' : ''}{fmtFull(repPeriodComparison.totals.delta)}</td>
                    <td><span className={repPeriodComparison.totals.pct >= 0 ? 'pos' : 'neg'}>{repPeriodComparison.totals.pct >= 0 ? '+' : ''}{repPeriodComparison.totals.pct.toFixed(1)}%</span></td>
                    <td />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DevOverlay>

      <DevOverlay name="Customer ARR Health Heatmap">
        <div className="arr-chart-card arr-health-heatmap-card">
          <div className="arr-health-heatmap-head">
            <div>
              <div className="arr-chart-title">Customer ARR Health Heatmap</div>
              <div className="arr-chart-sub">Monthly ARR per customer — 🟢 growing · 🔴 declining · ⬜ no ARR</div>
            </div>
            <label className="arr-health-limit">
              Show
              <select value={heatmapLimit} onChange={event => setHeatmapLimit(Number(event.target.value))}>
                <option value={10}>Top 10</option>
                <option value={15}>Top 15</option>
                <option value={25}>Top 25</option>
                <option value={50}>Top 50</option>
              </select>
            </label>
          </div>
          <div className="arr-health-heatmap-wrap">
            <table className="arr-health-heatmap-table">
              <thead>
                <tr>
                  <th className="arr-health-customer-th">Customer</th>
                  {customerHealthHeatmap.months.map(month => <th key={month}>{month.slice(2)}</th>)}
                </tr>
              </thead>
              <tbody>
                {customerHealthHeatmap.rows.map(row => (
                  <tr key={row.customer}>
                    <td className="arr-health-customer" title={row.customer}>{row.shortName}</td>
                    {row.cells.map(cell => (
                      <td className={`arr-health-cell ${cell.status}`} key={`${row.customer}-${cell.month}`}>
                        {cell.value > 0 ? shortMoney(cell.value) : ''}
                      </td>
                    ))}
                  </tr>
                ))}
                {!customerHealthHeatmap.rows.length && (
                  <tr><td colSpan={customerHealthHeatmap.months.length + 1} className="bm-empty-row">No customer health data available.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DevOverlay>

      <div className="arr-action-grid">
        <DevOverlay name="At-Risk Customers">
          <div className="arr-chart-card arr-action-card">
            <div className="arr-action-title">⚠️ At-Risk Customers</div>
            <div className="arr-action-sub">Declining ARR for 2+ consecutive months</div>
            <div className="arr-action-table-wrap">
              <table className="arr-action-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Rep</th>
                    <th>Current ARR</th>
                    <th>Decline</th>
                    <th>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {customerActionLists.atRisk.map(row => {
                    const badgeClass = row.risk === 'high' ? 'high' : row.risk === 'med' ? 'med' : 'low';
                    const badgeText = row.risk === 'high' ? 'High Risk' : row.risk === 'med' ? 'Watch' : 'Monitor';
                    return (
                      <tr key={row.customer}>
                        <td className="arr-action-name">{row.customer}</td>
                        <td>{row.rep || '-'}</td>
                        <td className="arr-action-money">{fmtFull(row.current)}</td>
                        <td className="arr-action-money danger">{fmtFull(row.decline)}</td>
                        <td><span className={`arr-risk-badge ${badgeClass}`}>{badgeText}</span></td>
                      </tr>
                    );
                  })}
                  {!customerActionLists.atRisk.length && (
                    <tr><td colSpan={5} className="arr-action-empty success">No at-risk customers detected</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </DevOverlay>

        <DevOverlay name="New Logos This Period">
          <div className="arr-chart-card arr-action-card">
            <div className="arr-action-title">🆕 New Logos for Selected Period</div>
            <div className="arr-action-sub">Customers with positive New ARR in the period</div>
            <div className="arr-action-table-wrap">
              <table className="arr-action-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Product</th>
                    <th>Rep</th>
                    <th>New ARR</th>
                    <th>Since</th>
                  </tr>
                </thead>
                <tbody>
                  {customerActionLists.newLogos.map(row => (
                    <tr key={`${row.customer}-${row.product}`}>
                      <td className="arr-action-name">{row.customer}</td>
                      <td>{row.product}</td>
                      <td>{row.rep || '-'}</td>
                      <td className="arr-action-money success">+{fmtFull(row.arr)}</td>
                      <td className="arr-action-date">{row.since}</td>
                    </tr>
                  ))}
                  {!customerActionLists.newLogos.length && (
                    <tr><td colSpan={5} className="arr-action-empty">No new logos in this period</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </DevOverlay>
      </div>

      <DevOverlay name="Expansion Candidates">
        <div className="arr-chart-card arr-action-card arr-expansion-card">
          <div className="arr-action-title">🎯 Expansion Opportunities</div>
          <div className="arr-action-sub">Customers active 12+ months with no upsell recorded — strong expansion potential</div>
          <div className="arr-action-table-wrap">
            <table className="arr-action-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Rep</th>
                  <th>Current ARR</th>
                  <th>Active Since</th>
                  <th>Months</th>
                </tr>
              </thead>
              <tbody>
                {customerActionLists.expansion.map(row => (
                  <tr key={row.customer}>
                    <td className="arr-action-name">{row.customer}</td>
                    <td>{row.rep || '—'}</td>
                    <td className="arr-action-money primary">{fF(row.arr)}</td>
                    <td className="arr-action-date">{row.first}</td>
                    <td className={`arr-action-tenure ${row.months >= 24 ? 'strong' : ''}`}>{row.months}m</td>
                  </tr>
                ))}
                {!customerActionLists.expansion.length && (
                  <tr><td colSpan={5} className="arr-action-empty">No upsell opportunities found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DevOverlay>
    </>
  );
}

export default InsightsTab;
export { InsightsTab };
